import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useAuth0} from '@auth0/auth0-react'

import {TabToggle} from '../../../shared/Tabs'
import {Button} from '../../../shared/Button'
import {Spinner} from '../../../../components/loader-spinner'
import {useAuthClient} from '../../../../utils/use-auth-client'
import Drawer from '../../../shared/Drawer'
import AccountDetailsDrawer from '../components/AccountDetailsDrawer'
import SubscriptionDetailsDrawer from '../components/SubscriptionDrawer'
import Modal from '../../../shared/Modal'
import {Modal as PastDueInvoiceModal} from '@thryvlabs/maverick'
import {
  hasBlockingPastDueInvoice,
  SHOW_INVOICE_INFO_FOR_PAST_DUE,
} from '../../../../utils/past-due-invoice'
import {InvoiceInformation} from '../../../../components/account-information-components/invoice-information'
import {AccountEditModal} from '../components/AccounEditModal'
import {
  formValuesToBillingPayload,
  applyValuesToAccount,
  locationToFormValues,
  clearPartnerAttribution,
} from '../AllAccounts/AllAccountsFormMapping'
import {
  ContentArea,
  Toolbar,
  SearchWrapper,
  SearchRow,
  AccountList,
  StatusMessage,
  SectionTitle,
  PaginationBar,
  PaginationInfo,
  PaginationControls,
  PageButton,
  SectionHeader,
} from '../AccountsTab.styles'
import {fetchMyAccounts} from '../../../../app-store/accounts/accountsThunk'
import {accountsApi} from '../../../../app-store/accounts/accountsApi'
import {useDispatch, useSelector} from 'react-redux'
import {AccountRow} from '../components/AccountRow'
import {
  setStep,
  setCountrySelectState,
  setSelectedCountryStates,
  selectCountrySelectState,
} from '../../../../app-store/AccountSetupSlice'
import countryRegionJSON from '../../../../utils/country-region.json'
import {fetchSelectedCountryRegionList} from '../../AccountInfo/services/locationAPI'
import {Input} from '../../../shared/Input'
import {
  StyledAddIcon,
  StyledSearchIcon,
} from '../../../shared/icons/icons.styles'
import useDebounce from '../../../../utils/useDebounce'
import {useAsync} from '../../../../utils/use-async'
import {useAddressValidation} from '../hooks/useAddressValidation'
import {useUserFeatures} from '../../../../context/user-features-context'
import {isRestrictedAccountCreationCountry} from '../../AccountInfo/AccountInfo'
import {
  SchedulingLayout,
  LocationTabList,
  LocationTabCard,
  LocationTabRow,
  LocationTabMainIcon,
  LocationTabDetailsCol,
  LocationTabTopRow,
  LocationTabIndexLabel,
  LocationTabName,
  LocationTabBadge,
  LocationDetailStack,
} from '../../AccountSetup/components/SchedulingStep.styles'
import Icon from '../../../../core/shared/Icon'
import {ResolvedLocationSummary} from '../components/ResolvedLocationSummary'
import {buildPurchaseLinkState} from '../purchaseLinkState'
import {resolveLocationAccountId} from '../locationIdentity'
import {
  setAccountInfo,
  initLocations,
  setProductConfigInfo,
  setIsUpgrade,
  setSalesAttributionFields,
} from '../../../../app-store/PurchaseLinkSlice'
import {applyProductRulesBeforeLoading} from '../../../../features/CatalogGrid/utils/productPreloadRules'
import {fetchKeapPartnerCatalog} from '../../../../utils/fetch-keap-partner-catalog'
import {
  selectEffectiveRole,
  selectUserRoles,
} from '../../../../app-store/selector'
import {getTaxIdentifier} from '../../AccountInfo/utils/taxIdentifier'
import {buildBehalfRepUpgradeFields} from '../../AccountInfo/utils/behalfRep'
import {
  getAccountManagementLandingPath,
  getAccountTabs,
  hasPartnerOpsRole,
  isAccountPathHiddenForRoles,
  shouldSkipOrderEditModal,
  shouldUseLegacyPartnerOpsFlow,
} from '../../../../utils/account-management-navigation'
const PAGE_SIZE = 20

const fetchCatalogPlans = ({client, isKeapPartner, country, roles}) =>
  isKeapPartner
    ? fetchKeapPartnerCatalog(client, {country: country || 'US', roles})
    : client('plans/5/v1/list')

const payBadgeStyle = {
  parent: {
    background: '#E6F1FB',
    color: '#0C447C',
    border: '0.5px solid #85B7EB',
  },
  self: {
    background: '#EAF3DE',
    color: '#27500A',
    border: '0.5px solid #97C459',
  },
}

const getLegacySubscriptionId = loc =>
  loc?.subscriptionId ||
  loc?.recurlySubscriptionId ||
  loc?.RECURLY_SUBSCRIPTION_ID ||
  loc?.uuid ||
  loc?.subscriptions?.find(s => s.subscriptionUuid)?.subscriptionUuid

// GEP-6957: country isn't editable in the edit modal, so every restricted-country
// check against an editingTarget (the render-time disabled state below, and the
// "Save & Proceed to Order" gate in handleSaveAndProceed) needs to read it off the
// same account/parentAccount fields the same way — including the `countryCode`
// fallback (some account shapes only carry it there, e.g. a partial/Keap-partner
// record) and the `selectedCountry` state that resolveEditCountry ultimately falls
// back to for the actual address-validation call. Without matching that fallback
// chain exactly, this check can read `undefined` (miss a restricted country) while
// runAddressValidation still resolves and submits the real country, which the
// backend's validateAddressSchema rejects with a raw Joi error instead of this
// modal's friendly message.
const getEditingTargetCountry = (target, selectedCountry) =>
  (target?.type === 'add-location'
    ? target?.parentAccount?.address?.country ||
      target?.parentAccount?.country ||
      target?.parentAccount?.countryCode
    : target?.account?.address?.country ||
      target?.account?.country ||
      target?.account?.countryCode) || selectedCountry

function toAccountRow(hierarchy) {
  const {parent, children} = hierarchy
  const company =
    parent?.company ||
    [parent?.firstName, parent?.lastName].filter(Boolean).join(' ')
  return {
    // Billing Service's code is the shared EAID; its id is the Recurly account
    // code used for parent-level payment-link operations.
    thryvId: parent?._fromBillingService
      ? parent?.id
      : parent?.code || parent?.id,
    accountCode: parent?._fromBillingService
      ? parent?.id
      : parent?.code || parent?.id,
    parentOrgId: parent?.parentOrgId,
    canAddLocation: parent?.canAddLocation === true,
    companyName: company,
    accountEmail: parent?.email,
    firstName: parent?.firstName,
    lastName: parent?.lastName,
    billTo: parent?.billTo ?? 'self',
    city: parent?.address?.city,
    stateCode: parent?.address?.region,
    country: parent?.address?.country,
    status: 'active',
    isPre5: parent?.isPre5 || false,
    locations: (children || [])
      .filter(c => !c.isSandbox)
      .map(child => {
        // Billing Service uses code for the parent EAID, shared by every child.
        // Its id is the individual location identifier and must drive selection,
        // address updates, and parent-pay purchase links.
        const childLocationId = child?._fromBillingService
          ? child?.id
          : child?.code || child?.id
        return {
          ...child,
          id: childLocationId,
          code: child?.code || childLocationId,
          thryvId: childLocationId,
          name:
            child?.locationName ||
            child?.company ||
            child?.companyName ||
            child?.name ||
            [child?.firstName, child?.lastName].filter(Boolean).join(' '),
          locationName: child?.locationName || child?.location_name || '',
          company: child?.company || child?.companyName || child?.name,
          firstName: child?.firstName,
          lastName: child?.lastName,
          email: child?.email,
          city: child?.address?.city,
          stateCode: child?.address?.region,
          country: child?.address?.country,
          billTo: child?.billTo ?? (child?.parentAccountId ? 'parent' : 'self'),
          subs: 0,
          subscriptions: [],
        }
      }),
  }
}

export function MyAccounts({
  isKeapPartner = false,
  onNewAccount,
  keapPartnerId,
  initialSearch = '',
  initialThryvId = null,
  initialAccountCodes = [],
  isEmbedded = false,
  onBack,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  // Keap Partner Control Center hosts the wizard in-pane (no route change) and
  // renders these results inside it. setStep(2) already moves that wizard on to
  // Catalog Selection; navigating away would unmount it, and its cleanup would
  // reset the step and the seeded locations, dropping the rep back on Email Lookup.
  const isHostedInWizard =
    isEmbedded && location.pathname !== '/accounts/account-setup'
  const client = useAuthClient()
  const {user} = useAuth0()
  const dispatch = useDispatch()
  const {actualRole} = useSelector(selectUserRoles)
  const effectiveRoles = useSelector(selectEffectiveRole)
  const userFeatures = useUserFeatures()
  const {Enable_International_Account_Creation} = userFeatures
  const accountTabs = getAccountTabs(effectiveRoles, userFeatures)
  const accountManagementLandingPath =
    getAccountManagementLandingPath(effectiveRoles)
  const [orderingThryvId, setOrderingThryvId] = useState(null)
  const [restrictedCountryModalOpen, setRestrictedCountryModalOpen] =
    useState(false)
  const [showPastDueInvoiceModal, setShowPastDueInvoiceModal] = useState(false)
  const [pastDueInvoiceContext, setPastDueInvoiceContext] = useState(null)

  const {
    results: accounts,
    total,
    loading,
    error,
  } = useSelector(state => state.accounts.myAccounts)

  const {selectedCountry} = useSelector(selectCountrySelectState)

  const [searchQuery, setSearchQuery] = useState(
    initialSearch ||
      // 'emailLookup': seeded from the Email Lookup step. 'returnFromAccountSetup':
      // Cancel from the account-setup wizard restoring the search the rep had
      // before "Create Purchase Link"/"New Account" (see handleCancel in
      // AccountSetup/index.js) — both just seed from the carried email/query.
      (['emailLookup', 'returnFromAccountSetup'].includes(
        location.state?.context,
      )
        ? location.state?.email ?? ''
        : ''),
  )
  const debouncedSearch = useDebounce(searchQuery, 300)
  // AccountSetup may pass a fresh empty array while rendering. Depend on its
  // stable value instead of the array identity so the fallback effect does not
  // repeatedly refetch the same results.
  const initialAccountCodesKey = Array.isArray(initialAccountCodes)
    ? initialAccountCodes.join('|')
    : ''
  const fallbackCodes = useMemo(
    () => [
      ...new Set(
        [
          initialThryvId,
          ...initialAccountCodesKey.split('|').filter(Boolean),
        ].filter(Boolean),
      ),
    ],
    [initialThryvId, initialAccountCodesKey],
  )

  useEffect(() => {
    if (
      !isEmbedded &&
      isAccountPathHiddenForRoles(location.pathname, effectiveRoles)
    ) {
      navigate(accountManagementLandingPath, {replace: true})
    }
  }, [
    accountManagementLandingPath,
    effectiveRoles,
    isEmbedded,
    location.pathname,
    navigate,
  ])

  // Fallback: when the partner-scoped search returns nothing but email lookup
  // resolved verified account code(s), fetch each hierarchy directly so the
  // partner can still see and order from it. Multiple codes are only supplied
  // by the API after they have been verified as partner-owned.
  const [fallbackAccounts, setFallbackAccounts] = useState(null)
  const [fallbackLoading, setFallbackLoading] = useState(false)

  useEffect(() => {
    if (!loading && total === 0 && fallbackCodes.length > 0) {
      let cancelled = false
      setFallbackLoading(true)
      // For Keap Partner, try partner-accounts with initialThryvId as search first
      // so link status (pending/complete/expired) is included. partner-accounts matches
      // oh.thryv_id (completed orders) and cco.thryv_id (pending orders) by the LOC code
      // returned from vCita, which is the child location the email lookup resolved to.
      // If partner-accounts finds nothing (e.g. account created but no link generated),
      // fall back to getAccounts to at least display the account hierarchy.
      const fetchFallback = Promise.all(
        fallbackCodes.map(async accountCode => {
          if (isKeapPartner && keapPartnerId) {
            const result = await accountsApi.getPartnerAccounts(client, {
              keapPartnerId,
              search: accountCode,
              offset: 0,
              limit: 20,
            })
            if (result?.accounts?.length > 0) return result.accounts
          }
          const hierarchies = await accountsApi.getAccounts(client, {
            thryvId: accountCode,
          })
          return hierarchies.map(toAccountRow)
        }),
      ).then(results => {
        const seenAccountCodes = new Set()
        return results.flat().filter(account => {
          const accountCode = account.accountCode || account.thryvId
          if (!accountCode || seenAccountCodes.has(accountCode)) return false
          seenAccountCodes.add(accountCode)
          return true
        })
      })

      fetchFallback
        .then(accounts => {
          if (!cancelled) setFallbackAccounts(accounts)
        })
        .catch(() => {
          if (!cancelled) setFallbackAccounts([])
        })
        .finally(() => {
          if (!cancelled) setFallbackLoading(false)
        })
      return () => {
        cancelled = true
      }
    } else {
      setFallbackAccounts(null)
      setFallbackLoading(false)
    }
  }, [loading, total, fallbackCodes, isKeapPartner, keapPartnerId, client])

  // Drawer state
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedLoc, setSelectedLoc] = useState(null)

  // Edit / Add modal state
  // editingTarget: { account, type, accountId, loading }
  const [editingTarget, setEditingTarget] = useState(null)
  const [editValues, setEditValues] = useState({})
  const [saving, setSaving] = useState(false)

  // Pagination (server-side via offset/limit)
  const [page, setPage] = useState(0)
  const totalPages = total ? Math.ceil(total / PAGE_SIZE) : 0
  const rangeStart = total ? page * PAGE_SIZE + 1 : 0
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, total ?? 0)
  const {run} = useAsync({status: 'idle'})
  const {validateAddress, isValidatingAddress} = useAddressValidation(
    client,
    run,
  )
  const [addressErrors, setAddressErrors] = useState({})
  // Set when Google's Address Validation API returns a plausible-but-not-
  // confirmed ("SUGGESTED") correction for the representative location's
  // address — rendered as an inline entered-vs-recommended banner inside
  // AccountEditModal instead of a separate confirm dialog.
  const [addressSuggestion, setAddressSuggestion] = useState(null)
  // Forces the hidden location/identity fields back into view when the
  // "Create Purchase Link" order-flow modal (hideLocationDetails) fails
  // required-field validation, so the rep can actually see and fix the
  // field the toast just told them about.
  const [revealLocationDetails, setRevealLocationDetails] = useState(false)
  // Non-null while reviewing a multi-location ("parentPay") purchase link
  // whose address validation flagged one or more locations — see
  // validateLocationsBatch/handleSaveReviewLocation below.
  const [multiLocationReview, setMultiLocationReview] = useState(null)
  // Set right before validation runs in handleSaveAndProceed vs handleSaveInfo
  // so that once a suggestion banner appears, handleConfirmSuggestion still
  // knows which button the rep originally clicked — otherwise "Use recommended"
  // always falls back to save+close even when the rep meant to proceed to order.
  const proceedAfterSuggestionRef = useRef(false)

  const resolveEditCountry = useCallback(() => {
    return editingTarget?.account?.address?.country || selectedCountry
  }, [editingTarget, selectedCountry])

  const runAddressValidation = useCallback(async () => {
    setAddressErrors({})
    setAddressSuggestion(null)
    const result = await validateAddress({
      ...editValues,
      country: resolveEditCountry(),
    })
    if (!result.success && result.fieldErrors) {
      setAddressErrors(result.fieldErrors)
      if (Object.keys(result.fieldErrors).length > 0) {
        setRevealLocationDetails(true)
      }
    }
    if (!result.success && result.suggestion) {
      setAddressSuggestion(result.suggestion)
      setRevealLocationDetails(true)
    }
    if (result.success && result.values) {
      setEditValues(prev => ({...prev, ...result.values}))
    }
    return result
  }, [validateAddress, editValues, resolveEditCountry])

  // Fetches fresh account data + runs address validation for every location in
  // a multi-location ("parentPay") purchase link — unlike runAddressValidation
  // above (which only ever checks the single representative location loaded
  // into editValues), this covers the rest of the locations riding along in
  // locationsOverride so a stale/invalid address on a non-representative
  // location isn't silently passed straight through to the order.
  const validateLocationsBatch = useCallback(
    async locations =>
      Promise.all(
        locations.map(async loc => {
          const locationId = resolveLocationAccountId(loc)
          let fresh = loc
          try {
            // account-details omits _fromBillingService. Retain the list row's
            // identity metadata so all later parent-pay operations keep using
            // this child's unique location id.
            fresh = {
              ...loc,
              ...(await accountsApi.getAccountById(client, locationId)),
            }
          } catch {
            // Fall back to the cached row if the fresh fetch fails — still
            // attempt validation against whatever address data we have.
          }
          const values = locationToFormValues(fresh)
          const result = await validateAddress({
            ...values,
            country: fresh?.address?.country || fresh?.country || loc?.country,
          })
          return {
            locationId,
            account: fresh,
            values: result.values || values,
            status: result.success ? 'verified' : 'needs-attention',
            fieldErrors: result.fieldErrors || {},
            suggestion: result.suggestion || null,
          }
        }),
      ),
    [client, validateAddress],
  )

  const setActiveReviewLocation = useCallback(locationId => {
    setMultiLocationReview(prev =>
      prev ? {...prev, activeLocationId: locationId} : prev,
    )
  }, [])

  const updateReviewLocationValues = useCallback((locationId, values) => {
    setMultiLocationReview(prev =>
      prev
        ? {
            ...prev,
            valuesByLocation: {...prev.valuesByLocation, [locationId]: values},
          }
        : prev,
    )
  }, [])

  const handleNewAccount = useCallback(() => {
    if (onNewAccount) {
      onNewAccount()
      return
    }
    navigate('/accounts/account-setup', {
      state: {returnTo: '/accounts/my-accounts', returnSearch: searchQuery},
    })
  }, [onNewAccount, navigate, searchQuery])

  // Dispatch the Redux country + states so the State dropdown is enabled.
  const loadCountryStates = useCallback(
    country => {
      const resolved = country || 'US'
      dispatch(setCountrySelectState(resolved))
      dispatch(
        setSelectedCountryStates(
          fetchSelectedCountryRegionList(resolved, countryRegionJSON),
        ),
      )
    },
    [dispatch],
  )

  // Open modal immediately with cached data, then replace with fresh fetch.
  const openEditModal = useCallback(
    async (
      cachedAccount,
      type,
      {
        fromOrder = false,
        orderMode,
        locationsOverride,
        parentAccount,
        blankAttribution = false,
      } = {},
    ) => {
      const accountId = cachedAccount?.thryvId
      const cachedCountry =
        cachedAccount?.country ||
        cachedAccount?.address?.country ||
        cachedAccount?.countryCode ||
        'US'
      // GEP-6957: block plain "Edit" too, not just "Create Purchase Link" —
      // Save Information is permanently disabled for a restricted-country
      // account regardless of how this modal was reached (see
      // isEditCountryRestricted below), so opening it can never lead anywhere
      // productive. Show the Client Care modal immediately instead of a form
      // where every button is a dead end. Redundant-safe for the order-flow
      // callers (handleOrder/handleOrderForParent already checked this before
      // calling here) — this is what actually gates the plain-Edit entry
      // point, which never goes through handleOrder at all.
      if (
        isRestrictedAccountCreationCountry(
          cachedCountry,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }
      setEditValues({})
      setRevealLocationDetails(false)
      setAddressSuggestion(null)
      setMultiLocationReview(null)
      // cachedAccount preserved so handleSaveAndProceed can pull subscription IDs for navigation
      // blankAttribution (PartnerOps): the XP / Affiliate Code field starts empty rather
      // than prefilled from the account.
      setEditingTarget({
        account: blankAttribution
          ? clearPartnerAttribution(cachedAccount)
          : cachedAccount,
        cachedAccount,
        type,
        accountId,
        loading: true,
        fromOrder,
        // Captured from the cached row now because the fresh getAccountById result
        // below (billing-service shape) does not carry isPre5.
        isPre5: !!cachedAccount?.isPre5,
        // handleOrder always sets orderMode + parentAccount (the true parent
        // it had in scope) here, since `account` above is just the single
        // clicked location, not the parent — handleSaveAndProceed needs both
        // to rebuild the purchase link against the real parent. Reuses the
        // same `parentAccount` field the add-location branch already relies
        // on as an account override. locationsOverride is only set for the
        // account-header "Create Purchase Link" flow (see
        // handleOrderForParent), which combines multiple parent-pay children
        // into one order.
        orderMode,
        locationsOverride,
        parentAccount,
      })
      // Pre-seed state dropdown from cached country while fetch is in flight
      loadCountryStates(cachedCountry)
      try {
        const fresh = await accountsApi.getAccountById(client, accountId)
        // Billing service sometimes returns address.region as "" even when the
        // list API has the correct stateCode. Patch it from the cached value so
        // the edit form pre-fills state correctly.
        const patchedFresh =
          fresh?.address && !fresh.address.region
            ? {
                ...fresh,
                address: {
                  ...fresh.address,
                  region:
                    cachedAccount?.stateCode ||
                    cachedAccount?.address?.region ||
                    '',
                },
              }
            : fresh
        setEditingTarget(prev =>
          prev?.accountId === accountId
            ? {
                ...prev,
                account: blankAttribution
                  ? clearPartnerAttribution({...cachedAccount, ...patchedFresh})
                  : {...cachedAccount, ...patchedFresh},
                loading: false,
              }
            : prev,
        )
        // Re-seed with accurate country from fresh billing-service response
        if (fresh?.country || fresh?.address?.country) {
          loadCountryStates(fresh.country || fresh.address?.country)
        }
      } catch {
        // Fall back silently to cached data if fetch fails
        setEditingTarget(prev =>
          prev?.accountId === accountId ? {...prev, loading: false} : prev,
        )
      }
    },
    [client, loadCountryStates, Enable_International_Account_Creation],
  )

  const handleEditParent = useCallback(
    (account, options) => openEditModal(account, 'parent', options),
    [openEditModal],
  )

  const handleEditChild = useCallback(
    (loc, options) => openEditModal(loc, 'child', options),
    [openEditModal],
  )

  // PartnerOps always gets the XP / Affiliate Code step on Create Purchase Link (same as
  // All Accounts), with the field empty. Returns true when the modal was opened and the
  // order flow must stop; Save & Proceed in that modal resumes it.
  const requirePartnerOpsXpCode = useCallback(
    (loc, orderContext) => {
      if (!hasPartnerOpsRole(actualRole)) return false
      setOrderingThryvId(null)
      handleEditChild(loc, {
        fromOrder: true,
        ...orderContext,
        blankAttribution: true,
      })
      return true
    },
    [actualRole, handleEditChild],
  )

  const handleOrder = useCallback(
    async (
      loc,
      parentAccount,
      {locationsOverride, orderMode = 'location'} = {},
    ) => {
      // GEP-6957: block "Create Purchase Link" for accounts already in an
      // unsupported (non-prod) country while international account creation
      // is off — same flag/helper AccountInfo.js uses to hide unsupported
      // countries from the new-account dropdown. This must run before every
      // other branch below (including the Keap Partner catalog redirect) so
      // restricted accounts never reach an order.
      const orderCountry =
        loc?.country ||
        loc?.address?.country ||
        loc?.countryCode ||
        parentAccount?.country ||
        parentAccount?.address?.country ||
        parentAccount?.countryCode
      if (
        isRestrictedAccountCreationCountry(
          orderCountry,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }

      if (!loc?.thryvId) {
        toast.error('Unable to start an upgrade without a Thryv ID.')
        return
      }

      // Checked here, before the edit-modal branch below,
      // so "Create Purchase Link" blocks up front instead of opening the edit
      // modal first — the past-due modal previously showed underneath that
      // still-open modal.
      if (await hasBlockingPastDueInvoice(client, loc.thryvId, orderCountry)) {
        setPastDueInvoiceContext({
          thryvId: loc.thryvId,
          country: orderCountry,
          email:
            loc?.email ||
            loc?.accountEmail ||
            parentAccount?.email ||
            parentAccount?.accountEmail,
        })
        setShowPastDueInvoiceModal(true)
        return
      }

      // Keap Partner orders intentionally use the same Purchase Link wizard
      // as Core OOE. The former KP11 redirect here bypassed Catalog Selection,
      // Scheduling, and Payment Details for every existing location.
      // Reps outside SKIP_ORDER_EDIT_MODAL_ROLES review/complete the location's (or, for a
      // pre-5 account, the account's) details in the Edit modal before a purchase link is
      // generated — "Save & Proceed to Order" in that modal runs the same navigation below.
      if (!shouldSkipOrderEditModal(effectiveRoles)) {
        if (parentAccount?.isPre5) {
          handleEditParent(parentAccount, {fromOrder: true})
        } else {
          handleEditChild(loc, {
            fromOrder: true,
            orderMode,
            parentAccount,
            ...(locationsOverride && {locationsOverride}),
          })
        }
        return
      }
      setOrderingThryvId(loc.thryvId)
      try {
        let billingAccountId = getLegacySubscriptionId(loc)
        // A location that already carries a subscription id must still be
        // checked for v5 — otherwise v5 accounts with subscriptions skip the
        // shared Purchase Link wizard and land on the legacy /order/upgrade
        // catalog. If that check fails, keep the legacy flow.
        let target = billingAccountId
          ? await client(`accounts/upgradeTarget?thryvId=${loc.thryvId}`, {
              method: 'GET',
            }).catch(() => null)
          : null
        if (!billingAccountId || target?.v5upgrade) {
          target =
            target ||
            (await client(`accounts/upgradeTarget?thryvId=${loc.thryvId}`, {
              method: 'GET',
            }))

          if (target?.v5upgrade) {
            const [accountSubscriptionsResponse, all5PlanPrices] =
              await Promise.all([
                client(`accounts/5/subscriptions?thryvId=${loc.thryvId}`),
                fetchCatalogPlans({
                  client,
                  isKeapPartner,
                  country: orderCountry || selectedCountry,
                  roles: effectiveRoles,
                }).catch(() => null),
              ])
            if (
              requirePartnerOpsXpCode(loc, {
                orderMode,
                locationsOverride,
                parentAccount,
              })
            ) {
              return
            }
            // PartnerOps accounts get accountInfo.xpCode auto-populated server-side
            // from the account's original keap_partner_links.keap_partner_id (see
            // validatePartnerOpsAccount / account-service.js's getAccountSubscriptions)
            // — that join can only ever produce a Keap Partner ID, so mark
            // salesRepOrPartner accordingly so the backend routes it to the right
            // Recurly custom field.
            const serverXpCode =
              accountSubscriptionsResponse?.accountInfo?.xpCode
            const accountSubscriptions = serverXpCode
              ? {
                  ...accountSubscriptionsResponse,
                  accountInfo: {
                    ...accountSubscriptionsResponse.accountInfo,
                    salesRepOrPartner: 'keap-partner',
                  },
                }
              : accountSubscriptionsResponse
            if (shouldUseLegacyPartnerOpsFlow(effectiveRoles)) {
              navigate('/order/upgrade', {
                state: {
                  order: {
                    thryvId: loc.thryvId,
                    v5upgrade: true,
                    billTo: accountSubscriptions?.accountInfo?.billTo,
                    CCAccountInfo: {thryvId: loc.thryvId},
                    accountSubscriptionData: accountSubscriptions,
                  },
                  navFrom: 'accounts',
                  currentStep: 1,
                  prefetchedPlanPrices: all5PlanPrices,
                  prefetchedAccountSubscriptions: accountSubscriptions,
                },
              })
              return
            }
            const purchaseLink = buildPurchaseLinkState({
              parentAccount: parentAccount,
              locationsOverride,
              accountSubscriptions,
              selectedLocation: loc,
              orderMode,
            })
            // Reuses Account Setup's Catalog Selection step (step 3) instead
            // of a dedicated route — jump straight there with
            // PurchaseLinkSlice pre-seeded, same as handleSingleLocNext does
            // for a brand-new account (see AccountSetup/index.js).
            dispatch(
              setProductConfigInfo(
                applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
              ),
            )
            dispatch(setAccountInfo(purchaseLink.accountInfo))
            dispatch(
              setSalesAttributionFields(purchaseLink.salesAttributionFields),
            )
            dispatch(
              initLocations({
                locations: purchaseLink.locations,
                selectedLocationId: purchaseLink.selectedLocationId,
                orderMode,
              }),
            )
            dispatch(setIsUpgrade(true))
            dispatch(setStep(2))
            if (!isHostedInWizard) {
              navigate('/accounts/account-setup', {
                state: {
                  returnTo: '/accounts/my-accounts',
                  returnSearch: searchQuery,
                },
              })
            }
            return
          }

          if (target?.subscriptionId) {
            billingAccountId = target.subscriptionId
          } else {
            toast.error('Unable to find a subscription for this location.')
            setOrderingThryvId(null)
            return
          }
        }
        // Pre-fetch subscription + plan prices in parallel while the spinner
        // is visible — passes results through route state so the catalog page
        // skips its own loading skeleton entirely.
        const [subscriptionData, planPrices] = await Promise.all([
          client(`subscription/uuid-${billingAccountId}`),
          fetchCatalogPlans({
            client,
            isKeapPartner,
            country: loc?.country || parentAccount?.country || selectedCountry,
            roles: effectiveRoles,
          }),
        ])
        subscriptionData.thryvID = subscriptionData.thryvID || loc.thryvId
        const planCode = subscriptionData?.planCode || ''
        const isCCPlanForNav =
          planCode.includes('-5-') || planCode.includes('-6-')
        const thryvIdForSubs = subscriptionData.thryvID
        const [emailValidation, accountSubscriptions] = await Promise.all([
          client(`orders/validate-email/${subscriptionData.recurlyEmail}`),
          isCCPlanForNav
            ? client(`accounts/5/subscriptions?thryvId=${thryvIdForSubs}`)
            : Promise.resolve(null),
        ])
        navigate('/order/upgrade', {
          state: {
            order: {
              thryvId: loc.thryvId,
              billingAccountId,
              v5upgrade: false,
              accountSearchResult: {account: loc},
              ...(isCCPlanForNav && {
                CCAccountInfo: {thryvId: subscriptionData.thryvID},
                template: subscriptionData?.industry || undefined,
              }),
            },
            navFrom: 'accounts',
            currentStep: 1,
            prefetchedAccountInfo: subscriptionData,
            prefetchedEmailValidation: emailValidation,
            prefetchedPlanPrices: planPrices,
            prefetchedAccountSubscriptions: accountSubscriptions,
          },
        })
      } catch (err) {
        toast.error(err?.message || 'Unable to load order.')
        setOrderingThryvId(null)
      }
    },
    [
      navigate,
      isHostedInWizard,
      dispatch,
      isKeapPartner,
      keapPartnerId,
      client,
      effectiveRoles,
      handleEditParent,
      handleEditChild,
      requirePartnerOpsXpCode,
      Enable_International_Account_Creation,
      userFeatures,
      searchQuery,
      selectedCountry,
    ],
  )

  // Header-level "Create Purchase Link" for accounts with more than one
  // parent-pay child — mirrors AllAccounts' handleOrderForParent. A single
  // (or no) parent-pay child has nothing to combine, so AccountRow's
  // hasParentPayChild already hides the button in that case; this guard is
  // just defensive parity. It passes every parent-pay child into the shared
  // core catalog with orderMode: 'parentPay', including Keap Partner accounts.
  const handleOrderForParent = useCallback(
    account => {
      const parentPayLocations = (account.locations ?? []).filter(
        l =>
          !['self', 'child', 'self-pay', 'self pay'].includes(
            String(l?.billTo ?? 'parent')
              .trim()
              .toLowerCase(),
          ),
      )
      if (parentPayLocations.length <= 1) return
      handleOrder(parentPayLocations[0], account, {
        locationsOverride: parentPayLocations,
        orderMode: 'parentPay',
      })
    },
    [handleOrder],
  )

  const handleAddLocation = useCallback(
    parentAccount => {
      const country =
        parentAccount?.address?.country ||
        parentAccount?.country ||
        parentAccount?.countryCode ||
        'US'
      // GEP-6957: same restricted-country gate as handleOrder — a parent
      // already in an unsupported country shouldn't get as far as the
      // add-location form (its "Save & Proceed to Order" is caught too, but
      // blocking here avoids the pointless intermediate form fill-out).
      if (
        isRestrictedAccountCreationCountry(
          country,
          Enable_International_Account_Creation,
        )
      ) {
        setRestrictedCountryModalOpen(true)
        return
      }
      setEditValues({})
      loadCountryStates(country)
      const parentEmail =
        parentAccount?.accountEmail || parentAccount?.email || ''
      setEditingTarget({
        account: {email: parentEmail},
        type: 'add-location',
        parentAccount,
        loading: false,
      })
    },
    [loadCountryStates, Enable_International_Account_Creation],
  )

  const handleEditClose = useCallback(() => {
    setEditingTarget(null)
    setEditValues({})
    setAddressErrors({})
    setAddressSuggestion(null)
    setRevealLocationDetails(false)
    setMultiLocationReview(null)
  }, [])

  // Returns { ok, response } on all paths.
  const saveAccount = useCallback(
    async ({manageSaving = true, resolvedValues, timeZone} = {}) => {
      const {accountId, account, type, parentAccount} = editingTarget || {}

      if (type === 'add-location') {
        if (manageSaving) setSaving(true)
        try {
          // Prefer the just-resolved address/timezone passed in explicitly over
          // `editValues` state — runAddressValidation's setEditValues hasn't been
          // through a re-render yet when saveAccount runs immediately after it in
          // the same handler, so reading state here would silently use the
          // pre-validation address and fall back to a hardcoded timezone.
          const values = resolvedValues || editValues
          const country =
            parentAccount?.country ||
            parentAccount?.address?.country ||
            selectedCountry ||
            'US'
          const billToRaw = values.billTo || 'parent'
          const payload = {
            parentCcId: parentAccount?.thryvId || null,
            parentOrgId: parentAccount?.parentOrgId || null,
            email:
              values.email ||
              parentAccount?.accountEmail ||
              parentAccount?.email ||
              null,
            firstName: values.firstName,
            lastName: values.lastName,
            company: values.businessName || null,
            phone: values.mobile || values.telephone || null,
            country,
            timeZone: timeZone || 'America/Chicago',
            address: {
              street: values.address || null,
              city: values.city || null,
              state: values.state || null,
              zipCode: values.postCode || null,
              country,
            },
            abn: getTaxIdentifier(values) || null,
            sfdcContactEmail: values.salesforceEmail || null,
            billTo: billToRaw === 'self' ? 'child' : billToRaw,
            locationName: values.locationName || null,
          }
          const response = await client('multi-location/location', {
            method: 'POST',
            data: payload,
          })
          return {ok: true, response}
        } catch (err) {
          toast.error('Failed to create location. Please try again.')
          return {ok: false, response: null}
        } finally {
          if (manageSaving) setSaving(false)
        }
      }

      if (!accountId) return {ok: true, response: null}
      if (manageSaving) setSaving(true)
      try {
        // Same rationale as the add-location branch above: prefer the
        // just-resolved values over `editValues` state in case this runs
        // before a setEditValues from the same validation pass has been
        // through a re-render.
        const values = resolvedValues || editValues
        const basePayload = formValuesToBillingPayload(values, account)
        const payload =
          type === 'child'
            ? // Send '' (not undefined) when cleared — the backend (intg-billing-client.js /
              // account-dao.js patchRecurlyAccount) treats `name` presence as authoritative for
              // both Company Name precedence and RECURLY_ACCOUNT.ACCOUNT_LOCATION_NAME, so an
              // intentional clear must actually reach it rather than being dropped as "unset".
              {...basePayload, name: values.locationName ?? ''}
            : basePayload
        await accountsApi.updateAccount(client, accountId, payload)
        return {ok: true, response: null}
      } catch (err) {
        toast.error('Failed to save account. Please try again.')
        return {ok: false, response: null}
      } finally {
        if (manageSaving) setSaving(false)
      }
    },
    [client, editingTarget, editValues, selectedCountry],
  )

  // Shared by handleSaveInfo (fresh validation) and handleConfirmSuggestion
  // (accepting a Google recommendation — saves with it directly instead of
  // re-running validateAddress on values Google already resolved) so the
  // post-save toast/close/refresh logic lives in one place.
  const finishSaveInfo = useCallback(
    async ({values, timeZone}) => {
      const {ok} = await saveAccount({
        resolvedValues: values,
        timeZone,
      })
      if (!ok) return
      const name =
        editingTarget?.type === 'add-location'
          ? 'Location added successfully.'
          : `${
              editValues.businessName || editingTarget?.account?.company || ''
            } updated successfully.`
      toast.success(name)
      setEditingTarget(null)
      setEditValues({})
      // Refresh the accounts list
      dispatch(
        fetchMyAccounts({
          client,
          offset: page * PAGE_SIZE,
          limit: PAGE_SIZE,
          keapPartnerId: isKeapPartner ? keapPartnerId : undefined,
          search: debouncedSearch || undefined,
        }),
      )
    },
    [
      saveAccount,
      editingTarget,
      editValues,
      dispatch,
      client,
      page,
      isKeapPartner,
      keapPartnerId,
      debouncedSearch,
    ],
  )

  const handleSaveInfo = useCallback(async () => {
    proceedAfterSuggestionRef.current = false
    const addressResult = await runAddressValidation()

    if (!addressResult.success) return
    await finishSaveInfo({
      values: addressResult.values,
      timeZone: addressResult.timeZone,
    })
  }, [runAddressValidation, finishSaveInfo])

  // Everything that happens once every location's address has been validated
  // (single location, or every location in a multi-location purchase link) —
  // shared by handleSaveAndProceed's happy path and handleProceedFromReview
  // (reached after every flagged address is individually resolved in the
  // stack review UI), so the Keap Partner / v5upgrade / legacy-subscription
  // order-building logic below
  // only exists in one place.
  const proceedToOrder = useCallback(
    async ({
      account,
      cachedAccount,
      type,
      editParentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      addressResult,
    }) => {
      if (type === 'add-location') {
        const emailForValidation =
          editValues.email ||
          editParentAccount?.accountEmail ||
          editParentAccount?.email ||
          null
        const capturedCountry =
          editParentAccount?.country ||
          editParentAccount?.address?.country ||
          selectedCountry ||
          'US'
        setSaving(true)
        try {
          const {ok, response} = await saveAccount({
            manageSaving: false,
            resolvedValues: addressResult?.values,
            timeZone: addressResult?.timeZone,
          })
          if (!ok) return
          if (response?.childProvisionFailed) {
            toast.warning(
              'Location request submitted. Provisioning is in progress — please try ordering once it appears in the list.',
            )
            setEditingTarget(null)
            setEditValues({})
            dispatch(
              fetchMyAccounts({
                client,
                offset: page * PAGE_SIZE,
                limit: PAGE_SIZE,
                keapPartnerId: isKeapPartner ? keapPartnerId : undefined,
                search: debouncedSearch || undefined,
              }),
            )
            return
          }
          const thryvId = response?.thryvId || response?.ccId
          if (!thryvId) {
            toast.error('Unable to start a new order without a Thryv ID.')
            return
          }
          if (
            await hasBlockingPastDueInvoice(client, thryvId, capturedCountry)
          ) {
            setPastDueInvoiceContext({
              thryvId,
              country: capturedCountry,
              email: emailForValidation,
            })
            setShowPastDueInvoiceModal(true)
            return
          }
          if (isKeapPartner) {
            try {
              if (keapPartnerId && user?.email && emailForValidation) {
                await client('cc/order/keapClient/createClientPartnerMap', {
                  data: {
                    link_id: 0,
                    keap_partner_id: keapPartnerId,
                    keap_partner_email: user.email,
                    keap_client_email: emailForValidation,
                    status: 'Created',
                  },
                })
              }
            } catch (mapErr) {
              console.error(
                'createClientPartnerMap (add-location) failed',
                mapErr,
              )
            }
          }
          // Land on the same Account Setup Catalog Selection step (step 3) that
          // "Create Purchase Link" uses for an existing location, instead of the
          // legacy standalone /order/new page — this is a brand-new location with
          // no subscriptions yet, so there's no upgradeTarget check to make first.
          const all5PlanPrices = await fetchCatalogPlans({
            client,
            isKeapPartner,
            country: capturedCountry || selectedCountry,
            roles: effectiveRoles,
          }).catch(() => null)
          const newLocation = {
            thryvId,
            code: thryvId,
            company: editValues.businessName || '',
            email: emailForValidation,
            country: capturedCountry,
            billTo: editValues.billTo === 'self' ? 'self' : 'parent',
          }
          const purchaseLink = buildPurchaseLinkState({
            parentAccount: editParentAccount,
            locationsOverride: [newLocation],
            accountSubscriptions: {},
            selectedLocation: newLocation,
            editValues,
          })
          setEditingTarget(null)
          setEditValues({})
          dispatch(
            setProductConfigInfo(
              applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
            ),
          )
          dispatch(setAccountInfo(purchaseLink.accountInfo))
          dispatch(
            setSalesAttributionFields(purchaseLink.salesAttributionFields),
          )
          dispatch(
            initLocations({
              locations: purchaseLink.locations,
              selectedLocationId: purchaseLink.selectedLocationId,
              orderMode: 'location',
            }),
          )
          dispatch(setStep(2))
          if (!isHostedInWizard) {
            navigate('/accounts/account-setup', {
              state: {
                returnTo: '/accounts/my-accounts',
                returnSearch: searchQuery,
              },
            })
          }
        } finally {
          setSaving(false)
        }
        return
      }

      setSaving(true)
      try {
        const {ok} = await saveAccount({
          manageSaving: false,
          resolvedValues: addressResult?.values,
          timeZone: addressResult?.timeZone,
        })
        if (!ok) return
        // Merge: cachedAccount carries subscription IDs (billingAccountId) needed by upgrade-order;
        // fresh account carries the authoritative thryvId from the billing service.
        const merged = {
          ...(cachedAccount || {}),
          ...(account?.thryvId ? {thryvId: account.thryvId} : {}),
        }
        // Fold what was just saved back onto the records the purchase link
        // is seeded from, so later pages don't show pre-edit account info.
        const savedValues = addressResult?.values || editValues
        const savedMerged = applyValuesToAccount(merged, savedValues)
        const loc =
          type === 'child'
            ? savedMerged
            : savedMerged?.locations?.[0] || savedMerged
        const parentActual = type === 'child' ? null : savedMerged
        const savedAccountId = resolveLocationAccountId(account)
        const locationsWithSaved = resolvedLocationsOverride?.map(l =>
          resolveLocationAccountId(l) === savedAccountId ? loc : l,
        )

        const upgradeCountry =
          loc?.country || loc?.address?.country || selectedCountry
        if (
          await hasBlockingPastDueInvoice(client, loc?.thryvId, upgradeCountry)
        ) {
          setPastDueInvoiceContext({
            thryvId: loc?.thryvId,
            country: upgradeCountry,
            email: loc?.email || loc?.accountEmail,
          })
          setShowPastDueInvoiceModal(true)
          return
        }

        if (!loc.thryvId) {
          toast.error('Unable to start an upgrade without a Thryv ID.')
          return
        }

        let billingAccountId = getLegacySubscriptionId(loc)
        // See handleOrder: a location with a subscription id must still be
        // checked for v5; on failure keep the legacy flow.
        let target = billingAccountId
          ? await client(`accounts/upgradeTarget?thryvId=${loc.thryvId}`, {
              method: 'GET',
            }).catch(() => null)
          : null
        if (!billingAccountId || target?.v5upgrade) {
          target =
            target ||
            (await client(`accounts/upgradeTarget?thryvId=${loc.thryvId}`, {
              method: 'GET',
            }))

          if (target?.v5upgrade) {
            const [accountSubscriptions, all5PlanPrices] = await Promise.all([
              client(`accounts/5/subscriptions?thryvId=${loc.thryvId}`),
              fetchCatalogPlans({
                client,
                isKeapPartner,
                country:
                  loc?.country || editParentAccount?.country || selectedCountry,
                roles: effectiveRoles,
              }).catch(() => null),
            ])
            if (shouldUseLegacyPartnerOpsFlow(effectiveRoles)) {
              setEditingTarget(null)
              setEditValues({})
              navigate('/order/upgrade', {
                state: {
                  order: {
                    thryvId: loc.thryvId,
                    v5upgrade: true,
                    billTo: accountSubscriptions?.accountInfo?.billTo,
                    CCAccountInfo: {thryvId: loc.thryvId},
                    accountSubscriptionData: accountSubscriptions,
                  },
                  navFrom: 'accounts',
                  currentStep: 1,
                  prefetchedPlanPrices: all5PlanPrices,
                  prefetchedAccountSubscriptions: accountSubscriptions,
                },
              })
              return
            }
            const purchaseLink = buildPurchaseLinkState({
              parentAccount: editParentAccount || parentActual,
              locationsOverride: locationsWithSaved,
              accountSubscriptions,
              selectedLocation: loc,
              editValues,
              orderMode: savedOrderMode,
            })
            setEditingTarget(null)
            setEditValues({})
            dispatch(
              setProductConfigInfo(
                applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
              ),
            )
            dispatch(setAccountInfo(purchaseLink.accountInfo))
            dispatch(
              setSalesAttributionFields(purchaseLink.salesAttributionFields),
            )
            dispatch(
              initLocations({
                locations: purchaseLink.locations,
                selectedLocationId: purchaseLink.selectedLocationId,
                orderMode: savedOrderMode,
              }),
            )
            dispatch(setIsUpgrade(true))
            dispatch(setStep(2))
            if (!isHostedInWizard) {
              navigate('/accounts/account-setup', {
                state: {
                  returnTo: '/accounts/my-accounts',
                  returnSearch: searchQuery,
                },
              })
            }
            return
          }

          if (target?.subscriptionId) {
            billingAccountId = target.subscriptionId
          } else {
            toast.error('Unable to find a subscription for this location.')
            return
          }
        }

        const [subscriptionData, planPrices] = await Promise.all([
          client(`subscription/uuid-${billingAccountId}`),
          fetchCatalogPlans({
            client,
            isKeapPartner,
            country:
              loc?.country || editParentAccount?.country || selectedCountry,
            roles: effectiveRoles,
          }),
        ])
        subscriptionData.thryvID = subscriptionData.thryvID || loc.thryvId
        const planCode = subscriptionData?.planCode || ''
        const isCCPlanForNav =
          planCode.includes('-5-') || planCode.includes('-6-')
        const thryvIdForSubs = subscriptionData.thryvID
        const [emailValidation, accountSubscriptions] = await Promise.all([
          client(`orders/validate-email/${subscriptionData.recurlyEmail}`),
          isCCPlanForNav
            ? client(`accounts/5/subscriptions?thryvId=${thryvIdForSubs}`)
            : Promise.resolve(null),
        ])
        setEditingTarget(null)
        setEditValues({})
        navigate('/order/upgrade', {
          state: {
            order: {
              thryvId: loc.thryvId,
              billingAccountId,
              v5upgrade: false,
              accountSearchResult: {account: loc},
              // On-behalf-of rep picked in the edit modal (no-op when none):
              // salesRepCode/behalf* are what the 4.0 upgrade capture reads.
              ...buildBehalfRepUpgradeFields(savedValues),
              ...(isCCPlanForNav && {
                CCAccountInfo: {thryvId: subscriptionData.thryvID},
                template: subscriptionData?.industry || undefined,
              }),
            },
            navFrom: 'accounts',
            currentStep: 1,
            prefetchedAccountInfo: subscriptionData,
            prefetchedEmailValidation: emailValidation,
            prefetchedPlanPrices: planPrices,
            prefetchedAccountSubscriptions: accountSubscriptions,
          },
        })
      } catch (err) {
        toast.error(err?.message || 'Unable to load order.')
      } finally {
        setSaving(false)
      }
    },
    [
      saveAccount,
      editValues,
      selectedCountry,
      navigate,
      isHostedInWizard,
      isKeapPartner,
      keapPartnerId,
      client,
      user,
      dispatch,
      page,
      debouncedSearch,
      userFeatures,
      searchQuery,
      effectiveRoles,
    ],
  )

  // "Use recommended address" on the AddressSuggestionBanner — Google
  // already resolved this exact recommendation (lat/lng/timeZone) in the
  // validateAddress call that produced the suggestion, so this saves with
  // it directly rather than re-running address validation, which is what
  // could hit an ambiguous-locality case a second time and loop.
  //
  // If the rep originally clicked "Save & Proceed to Order" (tracked via
  // proceedAfterSuggestionRef, since the suggestion banner interrupts that
  // flow before it can reach proceedToOrder), resume straight into the order
  // instead of just saving and closing the modal.
  const handleConfirmSuggestion = useCallback(
    async updatedValues => {
      const timeZone = addressSuggestion?.timeZone
      setAddressSuggestion(null)
      if (proceedAfterSuggestionRef.current) {
        const {
          account,
          cachedAccount,
          type,
          parentAccount: editParentAccount,
          orderMode: savedOrderMode,
          locationsOverride,
        } = editingTarget || {}
        await proceedToOrder({
          account,
          cachedAccount,
          type,
          editParentAccount,
          savedOrderMode,
          resolvedLocationsOverride: locationsOverride,
          addressResult: {success: true, values: updatedValues, timeZone},
        })
        return
      }
      await finishSaveInfo({values: updatedValues, timeZone})
    },
    [addressSuggestion, finishSaveInfo, editingTarget, proceedToOrder],
  )

  const handleSaveAndProceed = useCallback(async () => {
    // GEP-6957: the account's country isn't editable in this modal, so a
    // restricted-country account reaching this button (opened via plain "Edit"
    // rather than "Create Purchase Link", which already blocks at handleOrder)
    // must still be stopped here with the same "contact Client Care" modal
    // handleOrder shows, rather than silently no-op'ing.
    if (
      isRestrictedAccountCreationCountry(
        getEditingTargetCountry(editingTarget, selectedCountry),
        Enable_International_Account_Creation,
      )
    ) {
      setRestrictedCountryModalOpen(true)
      return
    }

    proceedAfterSuggestionRef.current = true
    const addressResult = await runAddressValidation()

    const {
      account,
      cachedAccount,
      type,
      parentAccount: editParentAccount,
      orderMode: savedOrderMode,
      locationsOverride,
    } = editingTarget || {}

    const isMultiLocation =
      type !== 'add-location' &&
      savedOrderMode === 'parentPay' &&
      (locationsOverride?.length || 0) > 1

    // A genuine required-field problem on this step's own fields (sales-rep
    // attribution) always needs fixing here first, regardless of how many
    // locations this purchase link covers.
    if (
      !addressResult.success &&
      Object.keys(addressResult.fieldErrors || {}).length
    ) {
      return
    }
    // Single location: an unconfirmed suggestion just reveals this form
    // (there's nothing to build a stack out of).
    if (!isMultiLocation && !addressResult.success) return

    // Multi-location ("parentPay") purchase link — runAddressValidation above
    // only ever checked the single representative `account`. Check every
    // other location riding along in locationsOverride too; if any of them
    // (including the representative itself) need attention, drop into the
    // stack review UI instead of continuing straight to the order.
    let resolvedLocationsOverride = locationsOverride
    if (isMultiLocation) {
      const representativeLocationId = resolveLocationAccountId(account)
      const otherLocations = locationsOverride.filter(
        loc => resolveLocationAccountId(loc) !== representativeLocationId,
      )
      setSaving(true)
      let batchResults
      try {
        batchResults = await validateLocationsBatch(otherLocations)
      } finally {
        setSaving(false)
      }
      const representativeEntry = {
        locationId: representativeLocationId,
        account,
        values: addressResult.values || editValues,
        status: addressResult.success ? 'verified' : 'needs-attention',
        fieldErrors: {},
        suggestion: addressResult.suggestion || null,
      }
      const allEntries = [representativeEntry, ...batchResults]
      const firstFailing = allEntries.find(e => e.status === 'needs-attention')
      if (firstFailing) {
        setMultiLocationReview({
          locations: allEntries.map(e => e.account),
          statuses: Object.fromEntries(allEntries.map(e => [e.locationId, e])),
          valuesByLocation: Object.fromEntries(
            allEntries.map(e => [e.locationId, e.values]),
          ),
          activeLocationId: firstFailing.locationId,
        })
        return
      }
      resolvedLocationsOverride = allEntries.map(e => e.account)
    }

    await proceedToOrder({
      account,
      cachedAccount,
      type,
      editParentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      addressResult,
    })
  }, [
    runAddressValidation,
    validateLocationsBatch,
    proceedToOrder,
    editingTarget,
    editValues,
    Enable_International_Account_Creation,
    selectedCountry,
  ])

  // True once every location in the stack review has been individually
  // saved — gates whether the footer shows "Save" (per-location) or
  // "Proceed to Order" (see handleSaveReviewLocation/handleProceedFromReview).
  const allReviewLocationsResolved = !!(
    multiLocationReview &&
    Object.values(multiLocationReview.statuses).every(
      e => e.status === 'verified',
    )
  )

  // The location backing whichever tab is active in the stack review, and
  // whether its address has already been verified — drives whether the
  // detail panel shows the full edit form or just a read-only recap.
  const activeReviewLocation = multiLocationReview
    ? multiLocationReview.locations.find(
        loc =>
          resolveLocationAccountId(loc) ===
          multiLocationReview.activeLocationId,
      ) || editingTarget?.account
    : null

  const isActiveReviewLocationResolved =
    !!multiLocationReview &&
    multiLocationReview.statuses[multiLocationReview.activeLocationId]
      ?.status === 'verified'

  // Shared by handleSaveReviewLocation's success path and
  // handleConfirmReviewSuggestion (accepting a Google recommendation without
  // re-validating it) — PATCHes the location and folds the result back into
  // multiLocationReview state so both callers stay in sync the same way.
  const persistReviewLocation = useCallback(
    async ({activeId, entry, finalValues}) => {
      // Fold the (possibly corrected) address back into the raw account shape
      // so downstream purchase-link building shows the address that was
      // actually validated/saved, not the stale fetched one.
      const updatedAccount = applyValuesToAccount(entry?.account, finalValues)
      // Mirrors saveAccount's own child-location payload exactly (see
      // above) — every location in this review is type 'child', so the
      // same locationName merge applies here.
      const basePayload = formValuesToBillingPayload(
        finalValues,
        updatedAccount,
      )
      await accountsApi.updateAccount(client, activeId, {
        ...basePayload,
        name: finalValues.locationName ?? '',
      })

      const updatedEntry = {
        ...entry,
        values: finalValues,
        status: 'verified',
        fieldErrors: {},
        suggestion: null,
        account: updatedAccount,
      }
      setMultiLocationReview(prev =>
        prev
          ? {
              ...prev,
              statuses: {...prev.statuses, [activeId]: updatedEntry},
              locations: prev.locations.map(loc =>
                resolveLocationAccountId(loc) === activeId
                  ? updatedAccount
                  : loc,
              ),
              valuesByLocation: {
                ...prev.valuesByLocation,
                [activeId]: finalValues,
              },
            }
          : prev,
      )
    },
    [client],
  )

  // Validates + saves just the currently-active location in the stack review
  // UI. Locations are fixed one at a time rather than all-at-once so the rep
  // can see each address resolve (or still need attention) immediately.
  const handleSaveReviewLocation = useCallback(async () => {
    if (!multiLocationReview) return
    const activeId = multiLocationReview.activeLocationId
    const entry = multiLocationReview.statuses[activeId]
    const values = multiLocationReview.valuesByLocation[activeId]

    setSaving(true)
    try {
      const result = await validateAddress({
        ...values,
        country: entry?.account?.address?.country || entry?.account?.country,
      })
      const finalValues = result.values || values

      if (result.success) {
        await persistReviewLocation({activeId, entry, finalValues})
        toast.success('Location saved.')
      } else {
        const updatedEntry = {
          ...entry,
          values: finalValues,
          status: 'needs-attention',
          fieldErrors: result.fieldErrors || {},
          suggestion: result.suggestion || null,
        }
        setMultiLocationReview(prev =>
          prev
            ? {
                ...prev,
                statuses: {...prev.statuses, [activeId]: updatedEntry},
                valuesByLocation: {
                  ...prev.valuesByLocation,
                  [activeId]: finalValues,
                },
              }
            : prev,
        )
        toast.error('This address still needs attention.')
      }
    } catch {
      toast.error('Failed to save this location. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [multiLocationReview, validateAddress, persistReviewLocation])

  // "Use recommended address" inside the multi-location review's
  // AddressSuggestionBanner — same rationale as handleConfirmSuggestion:
  // Google already resolved this recommendation in the validateAddress call
  // that produced it, so save it directly instead of re-validating and
  // risking the same ambiguous-locality loop a second time.
  const handleConfirmReviewSuggestion = useCallback(
    async updatedValues => {
      if (!multiLocationReview) return
      const activeId = multiLocationReview.activeLocationId
      const entry = multiLocationReview.statuses[activeId]
      setSaving(true)
      try {
        await persistReviewLocation({
          activeId,
          entry,
          finalValues: updatedValues,
        })
        toast.success('Location saved.')
      } catch {
        toast.error('Failed to save this location. Please try again.')
      } finally {
        setSaving(false)
      }
    },
    [multiLocationReview, persistReviewLocation],
  )

  // Reached from the stack review UI once every location has been
  // individually saved (see handleSaveReviewLocation) — each location was
  // already validated + PATCHed as it was resolved, so this just hands off
  // to the same order-building flow used by the single-location happy path.
  const handleProceedFromReview = useCallback(async () => {
    if (!multiLocationReview) return
    // GEP-6957: a parentPay bundle can carry a location whose own country is
    // restricted even when the representative location (the one handleOrder's
    // entry check saw) isn't — this stack-review step never re-checked the
    // other bundled locations, so a restricted one could ride straight through
    // to an order. Block here the same way handleOrder does.
    const hasRestrictedLocation = multiLocationReview.locations.some(loc =>
      isRestrictedAccountCreationCountry(
        loc?.address?.country || loc?.country || loc?.countryCode,
        Enable_International_Account_Creation,
      ),
    )
    if (hasRestrictedLocation) {
      setRestrictedCountryModalOpen(true)
      return
    }
    const {
      account,
      cachedAccount,
      type,
      parentAccount: editParentAccount,
      orderMode: savedOrderMode,
    } = editingTarget || {}
    const resolvedLocationsOverride = multiLocationReview.locations
    // The representative location's latest (review-screen) values — without
    // these proceedToOrder would re-save it from stale first-modal editValues.
    const representativeValues =
      multiLocationReview.valuesByLocation[resolveLocationAccountId(account)]
    setMultiLocationReview(null)
    await proceedToOrder({
      account,
      cachedAccount,
      type,
      editParentAccount,
      savedOrderMode,
      resolvedLocationsOverride,
      addressResult: {values: representativeValues},
    })
  }, [
    multiLocationReview,
    editingTarget,
    proceedToOrder,
    Enable_International_Account_Creation,
  ])

  const modalSubtitle =
    editingTarget?.type === 'add-location'
      ? editingTarget?.parentAccount?.company ||
        editingTarget?.parentAccount?.companyName
      : // The account-header "Create Purchase Link" flow opens this modal
      // scoped to a single representative location (see handleOrderForParent),
      // so editingTarget.account is just that one location — list every
      // parent-pay location this purchase link actually covers instead, so
      // the modal doesn't look like it's only asking about that one child.
      editingTarget?.locationsOverride
      ? editingTarget.locationsOverride
          .map(l => l?.name || l?.company || l?.companyName)
          .filter(Boolean)
          .join(', ')
      : editingTarget?.account?.name ||
        editingTarget?.account?.company ||
        editingTarget?.account?.companyName

  // "Create Purchase Link" (order) flow hides Save Information and always shows
  // Save & Proceed (even for a pre-5 parent, where it is normally hidden).
  const isOrderFlow = !!editingTarget?.fromOrder
  const showSaveInfoButton =
    editingTarget?.type !== 'add-location' && !isOrderFlow
  const showSaveAndProceedButton =
    editingTarget?.type !== 'parent' || isOrderFlow

  // GEP-6957: country isn't editable in this modal (AccountEditModal reads it
  // read-only off account/parentAccount — see its own `country` derivation),
  // so a restricted-country account can never be saved into a valid state here.
  // Disable both Save buttons rather than letting the rep save/proceed into an
  // order flow OOE can't actually support end to end for this country yet.
  const editCountry = getEditingTargetCountry(editingTarget, selectedCountry)
  const isEditCountryRestricted = isRestrictedAccountCreationCountry(
    editCountry,
    Enable_International_Account_Creation,
  )

  useEffect(() => {
    setPage(0)
  }, [isKeapPartner, keapPartnerId])

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch])

  useEffect(() => {
    dispatch(
      fetchMyAccounts({
        client,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        keapPartnerId: isKeapPartner ? keapPartnerId : undefined,
        search: debouncedSearch || undefined,
      }),
    )
  }, [dispatch, client, isKeapPartner, keapPartnerId, page, debouncedSearch])

  return (
    <>
      {!!orderingThryvId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Spinner />
        </div>
      )}
      <ContentArea>
        {isEmbedded ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '4px 0',
            }}
          >
            <Button variant="secondary-gray" size="sm" onClick={onBack}>
              &lt; Back
            </Button>
            {initialSearch && (
              <span style={{fontSize: '14px', color: '#555'}}>
                Results for: <strong>{initialSearch}</strong>
              </span>
            )}
          </div>
        ) : (
          <Toolbar>
            {isKeapPartner ? (
              <SectionHeader>
                <SectionTitle>My Accounts</SectionTitle>
                <SearchRow>
                  <SearchWrapper>
                    <Input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search accounts"
                      variant="form"
                      size="md"
                      leftIcon={<StyledSearchIcon />}
                      rightIcon={searchQuery ? '×' : null}
                      onRightIconClick={
                        searchQuery ? () => setSearchQuery('') : undefined
                      }
                    />
                  </SearchWrapper>
                  <Button
                    variant="solid-dark-blue"
                    size="sm"
                    onClick={handleNewAccount}
                  >
                    <StyledAddIcon />
                    <span>New Account</span>
                  </Button>
                </SearchRow>
              </SectionHeader>
            ) : (
              <>
                <TabToggle tabs={accountTabs} />
                <SearchRow>
                  <SearchWrapper>
                    <Input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search accounts"
                      variant="form"
                      size="md"
                      leftIcon={<StyledSearchIcon />}
                      rightIcon={searchQuery ? '×' : null}
                      onRightIconClick={
                        searchQuery ? () => setSearchQuery('') : undefined
                      }
                    />
                  </SearchWrapper>
                  <Button
                    variant="solid-dark-blue"
                    size="sm"
                    onClick={handleNewAccount}
                  >
                    <StyledAddIcon />
                    <span>New Account</span>
                  </Button>
                </SearchRow>
              </>
            )}
          </Toolbar>
        )}

        {(loading || fallbackLoading) && <Spinner />}

        {!loading && !fallbackLoading && error && (
          <StatusMessage>{error}</StatusMessage>
        )}

        {!loading &&
          !fallbackLoading &&
          !error &&
          total === 0 &&
          !fallbackAccounts && (
            <StatusMessage>
              {debouncedSearch
                ? 'No accounts match your search.'
                : 'No accounts found.'}
            </StatusMessage>
          )}

        {!loading &&
          !fallbackLoading &&
          !error &&
          fallbackAccounts?.length === 0 && (
            <StatusMessage>No accounts match your search.</StatusMessage>
          )}

        {!loading && !error && accounts.length > 0 && (
          <AccountList aria-label="My accounts">
            {accounts.map((account, index) => (
              <AccountRow
                key={account.thryvId || account.accountEmail || index}
                account={account}
                defaultOpen={!!debouncedSearch}
                onViewDetails={() => setSelectedAccount(account)}
                onViewSubscription={loc => setSelectedLoc(loc)}
                onOrder={loc => handleOrder(loc, account)}
                onOrderForParent={handleOrderForParent}
                onEdit={() => handleEditParent(account)}
                onEditChild={loc => handleEditChild(loc)}
                onAddLocation={() => handleAddLocation(account)}
                showStatus
                orderingThryvId={orderingThryvId}
              />
            ))}
          </AccountList>
        )}

        {!loading && !fallbackLoading && fallbackAccounts?.length > 0 && (
          <AccountList aria-label="Account results">
            {fallbackAccounts.map((account, index) => (
              <AccountRow
                key={account.thryvId || index}
                account={account}
                defaultOpen
                onViewDetails={() => setSelectedAccount(account)}
                onViewSubscription={loc => setSelectedLoc(loc)}
                onOrder={loc => handleOrder(loc, account)}
                onOrderForParent={handleOrderForParent}
                onEdit={() => handleEditParent(account)}
                onEditChild={loc => handleEditChild(loc)}
                onAddLocation={() => handleAddLocation(account)}
                showStatus
                orderingThryvId={orderingThryvId}
              />
            ))}
          </AccountList>
        )}

        {!loading && !error && totalPages > 1 && (
          <PaginationBar>
            <PaginationInfo>
              Showing {rangeStart}–{rangeEnd} of {total}
            </PaginationInfo>
            <PaginationControls>
              <PageButton
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ‹ Prev
              </PageButton>
              <PaginationInfo>
                Page {page + 1} of {totalPages}
              </PaginationInfo>
              <PageButton
                type="button"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next ›
              </PageButton>
            </PaginationControls>
          </PaginationBar>
        )}
      </ContentArea>

      {/* Account details drawer */}
      <Drawer
        isOpen={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        direction="right"
        size="600px"
      >
        {selectedAccount && (
          <AccountDetailsDrawer
            account={selectedAccount}
            onClose={() => setSelectedAccount(null)}
            payBadgeStyle={payBadgeStyle}
          />
        )}
      </Drawer>

      {/* Subscription details drawer */}
      <Drawer
        isOpen={!!selectedLoc}
        onClose={() => setSelectedLoc(null)}
        direction="right"
        size="600px"
      >
        {selectedLoc && (
          <SubscriptionDetailsDrawer
            loc={selectedLoc}
            onClose={() => setSelectedLoc(null)}
            source="my-accounts"
          />
        )}
      </Drawer>

      {/* Edit / Add location modal */}
      <Modal
        isOpen={!!editingTarget}
        onClose={saving || isValidatingAddress ? () => {} : handleEditClose}
        showCloseButton={!saving && !isValidatingAddress}
        closeOnBackdrop={false}
        closeOnEsc={!saving && !isValidatingAddress}
        title={
          multiLocationReview || (isOrderFlow && addressSuggestion)
            ? 'Manage Account'
            : isOrderFlow
            ? 'Is this sale on behalf of someone else?'
            : editingTarget?.type === 'add-location'
            ? 'Add New Location details'
            : editingTarget?.type === 'child'
            ? 'Edit Location details'
            : 'Edit Account details'
        }
        subtitle={multiLocationReview ? null : modalSubtitle}
        size="xl"
        width={multiLocationReview ? '1200px' : undefined}
        footerAlign="right"
        footer={
          multiLocationReview ? (
            allReviewLocationsResolved ? (
              <Button
                variant="solid-gradiant"
                onClick={handleProceedFromReview}
                disabled={saving || isValidatingAddress}
              >
                {saving ? 'Saving…' : 'Proceed to Order'}
              </Button>
            ) : (
              <Button
                variant="solid-gradiant"
                onClick={handleSaveReviewLocation}
                disabled={saving || isValidatingAddress}
              >
                {isValidatingAddress
                  ? 'Validating…'
                  : saving
                  ? 'Saving…'
                  : 'Save'}
              </Button>
            )
          ) : (
            <>
              {/* While a suggestion from "Save & Proceed to Order" is showing,
                  that button is relabelled "Save" — hide this one so the
                  footer doesn't show two near-identical save buttons. */}
              {showSaveInfoButton &&
                !(addressSuggestion && proceedAfterSuggestionRef.current) && (
                  <Button
                    variant="secondary-blue"
                    onClick={handleSaveInfo}
                    disabled={
                      saving ||
                      isValidatingAddress ||
                      editingTarget?.loading ||
                      isEditCountryRestricted
                    }
                  >
                    {isValidatingAddress
                      ? 'Validating…'
                      : saving
                      ? 'Saving…'
                      : 'Save Information'}
                  </Button>
                )}
              {showSaveAndProceedButton && (
                <Button
                  variant="solid-gradiant"
                  onClick={handleSaveAndProceed}
                  disabled={
                    saving || isValidatingAddress || editingTarget?.loading
                  }
                >
                  {isValidatingAddress
                    ? 'Validating…'
                    : saving
                    ? 'Saving…'
                    : addressSuggestion && proceedAfterSuggestionRef.current
                    ? 'Save'
                    : isOrderFlow
                    ? 'Continue'
                    : 'Save & Proceed to Order'}
                </Button>
              )}
            </>
          )
        }
      >
        {multiLocationReview ? (
          <SchedulingLayout style={{height: '65vh', overflow: 'hidden'}}>
            <LocationTabList style={{overflowY: 'auto', minHeight: 0}}>
              {multiLocationReview.locations.map((loc, index) => {
                const locId = resolveLocationAccountId(loc)
                const done =
                  multiLocationReview.statuses[locId]?.status === 'verified'
                return (
                  <LocationTabCard
                    key={locId}
                    as="button"
                    type="button"
                    active={locId === multiLocationReview.activeLocationId}
                    onClick={() => setActiveReviewLocation(locId)}
                  >
                    <LocationTabRow>
                      <LocationTabMainIcon>
                        <Icon
                          name="address"
                          size="sm"
                          color="#0327A9"
                          style={{width: 12, height: 12}}
                        />
                      </LocationTabMainIcon>
                      <LocationTabDetailsCol>
                        <LocationTabTopRow>
                          <LocationTabIndexLabel>
                            <Icon name="loc-ind" size="xxs" />
                            Location {index + 1}
                          </LocationTabIndexLabel>
                          <LocationTabBadge done={done}>
                            {done ? 'Resolved' : 'Not Resolved'}
                          </LocationTabBadge>
                        </LocationTabTopRow>
                        <LocationTabName>
                          {loc.locationName || loc.company || loc.companyName}
                        </LocationTabName>
                      </LocationTabDetailsCol>
                    </LocationTabRow>
                  </LocationTabCard>
                )
              })}
            </LocationTabList>
            <LocationDetailStack style={{overflowY: 'auto', minHeight: 0}}>
              {isActiveReviewLocationResolved ? (
                <ResolvedLocationSummary
                  name={
                    activeReviewLocation?.locationName ||
                    activeReviewLocation?.company ||
                    activeReviewLocation?.companyName
                  }
                  values={
                    multiLocationReview.valuesByLocation[
                      multiLocationReview.activeLocationId
                    ]
                  }
                />
              ) : (
                <AccountEditModal
                  key={multiLocationReview.activeLocationId}
                  roles={effectiveRoles}
                  account={activeReviewLocation}
                  type="child"
                  saving={saving}
                  isValidatingAddress={isValidatingAddress}
                  hideTypeBadge
                  additionalHiddenFields={['salesRepOrPartner', 'billTo']}
                  valuesOverride={
                    multiLocationReview.valuesByLocation[
                      multiLocationReview.activeLocationId
                    ]
                  }
                  onValuesChange={values =>
                    updateReviewLocationValues(
                      multiLocationReview.activeLocationId,
                      values,
                    )
                  }
                  addressSuggestion={
                    multiLocationReview.statuses[
                      multiLocationReview.activeLocationId
                    ]?.suggestion
                  }
                  onApplySuggestion={handleConfirmReviewSuggestion}
                  isOrderFlow={!!editingTarget?.fromOrder}
                  isPre5={!!editingTarget?.isPre5}
                />
              )}
            </LocationDetailStack>
          </SchedulingLayout>
        ) : (
          editingTarget && (
            <AccountEditModal
              key={editingTarget.accountId}
              roles={effectiveRoles}
              account={editingTarget.account}
              type={editingTarget.type}
              loading={editingTarget.loading}
              saving={saving}
              onValuesChange={setEditValues}
              parentAccount={editingTarget.parentAccount}
              isValidatingAddress={isValidatingAddress}
              addressErrors={addressErrors}
              addressSuggestion={addressSuggestion}
              onApplySuggestion={handleConfirmSuggestion}
              hideLocationDetails={
                !!editingTarget?.fromOrder && !revealLocationDetails
              }
              // Single-location counterpart of the multi-location review's
              // AccountEditModal: while an address suggestion is being
              // resolved here, sales-rep attribution and Bill To aren't
              // relevant yet, same as the stack review UI.
              hideTypeBadge={!!addressSuggestion}
              additionalHiddenFields={
                addressSuggestion ? ['salesRepOrPartner', 'billTo'] : []
              }
              isOrderFlow={!!editingTarget?.fromOrder}
              isPre5={!!editingTarget?.isPre5}
              suggestionApplyLabel={
                proceedAfterSuggestionRef.current
                  ? 'Use recommended address & Proceed to Order'
                  : undefined
              }
            />
          )
        )}
      </Modal>

      {/* GEP-6957: shown instead of proceeding to the order flow when
          "Create Purchase Link" is clicked for a restricted-country account. */}
      <Modal
        isOpen={restrictedCountryModalOpen}
        onClose={() => setRestrictedCountryModalOpen(false)}
        title="Almost there!"
        size="sm"
        footer={
          <Button
            variant="solid-gradiant"
            onClick={() => setRestrictedCountryModalOpen(false)}
          >
            OK
          </Button>
        }
      >
        Account upgrades for this country aren&apos;t available in OOE just yet.
        For now, please contact Client Care for assistance updating this
        account.
      </Modal>

      {showPastDueInvoiceModal &&
        (SHOW_INVOICE_INFO_FOR_PAST_DUE ? (
          <PastDueInvoiceModal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            hideX
            onClose={() => setShowPastDueInvoiceModal(false)}
          >
            <InvoiceInformation
              thryvId={pastDueInvoiceContext?.thryvId}
              email={pastDueInvoiceContext?.email}
              selectedCountry={pastDueInvoiceContext?.country}
              setIsInvoiceBilled={() => setShowPastDueInvoiceModal(false)}
            />
          </PastDueInvoiceModal>
        ) : (
          <PastDueInvoiceModal
            altBtn={<></>}
            openOnLoad
            variant="lg"
            footer
            action
            btnAction={() => setShowPastDueInvoiceModal(false)}
            onClose={() => setShowPastDueInvoiceModal(false)}
            btnActionText="Ok"
            hideX
          >
            <div style={{padding: '20px', textAlign: 'center'}}>
              <strong>This account has a past due invoice.</strong>
              <br></br>
              Please resolve the outstanding balance before processing a new
              order.
            </div>
          </PastDueInvoiceModal>
        ))}
    </>
  )
}
